import { createBrowserRouter, Navigate } from 'react-router-dom'
import { ReactNode, Suspense } from 'react'
import { lazyWithPreload } from 'react-lazy-with-preload'

import AuthRoute from '@/apollo/AuthRoute'
import InviteAuthGuard from '@/apollo/InviteAuthGuard'
import RootLayout from '@/components/RootLayout'
import { DocumentTitle } from '@/components'
import { IS_MAINTENANCE_ENABLED } from '@/constants'
import Error from '@/pages/Error/Error'
import CustomLoading from '@/components/Loading'
import { getAdminPanelFirstLink } from '@/pages/AdminPanel/utils/constants'
import {
  getDefaultNavItem,
  MY_REFERRALS_NAV_ITEMS,
} from '@/pages/MyReferralsV3/utils/constants'
import {
  sessionStorageService,
  SessionKey,
} from '@/services/SessionStorage/SessionStorageService'
import { useFeatureFlags } from '@/optimizely/useFeatureFlags'
import { FeatureFlag } from '@/optimizely/types'
import { RoutesData } from './RoutesData'
import { PATHS_WITH_SECTIONS } from './RoutesUtils'

// Lazy load all components
const CreateAccount = lazyWithPreload(
  () => import('@/pages/CreateAccount/CreateAccount')
)
CreateAccount.preload()

const MagicLinkActivate = lazyWithPreload(
  () => import('@/pages/MagicLinkActivate/MagicLinkActivate')
)

const EditPublicProfile = lazyWithPreload(
  () => import('@/pages/Profile/EditPublicProfile')
)
EditPublicProfile.preload()

const Marketplace = lazyWithPreload(() => import('@/pages/Marketplace'))
Marketplace.preload()

const PublicProfile = lazyWithPreload(
  () => import('@/pages/Profile/PublicProfile')
)
PublicProfile.preload()

const MyNetwork = lazyWithPreload(() => import('@/pages/MyNetwork'))
MyNetwork.preload()

const MyReferrals = lazyWithPreload(
  () => import('@/pages/MyReferralsV3/MyReferrals')
)
MyReferrals.preload()

const MyTasks = lazyWithPreload(() => import('@/pages/MyTasks/MyTasks'))
MyTasks.preload()

const CreateNewCase = lazyWithPreload(() => import('@/pages/CreateNewCase'))
CreateNewCase.preload()

const EditCase = lazyWithPreload(() => import('@/pages/EditCase'))
EditCase.preload()

const CreateProfile = lazyWithPreload(
  () => import('@/pages/CreateProfile/CreateProfile')
)
CreateProfile.preload()

const SignIn = lazyWithPreload(() => import('@/pages/SignIn/SignIn'))
SignIn.preload()

const SignOut = lazyWithPreload(() => import('@/pages/SignOut/SignOut'))
SignOut.preload()

const AccountSettings = lazyWithPreload(() => import('@/pages/AccountSettings'))
AccountSettings.preload()

const Case = lazyWithPreload(() => import('@/pages/Case'))
Case.preload()

const AdminPanel = lazyWithPreload(() => import('@/pages/AdminPanel'))
AdminPanel.preload()

const Verification = lazyWithPreload(
  () => import('@/pages/Verification/Verification')
)
Verification.preload()

const ForgotPassword = lazyWithPreload(
  () => import('@/pages/ForgotPassword/ForgotPassword')
)
ForgotPassword.preload()

const Maintenance = lazyWithPreload(
  () => import('@/pages/Maintenance/Maintenance')
)
Maintenance.preload()

const Analytics = lazyWithPreload(() => import('@/pages/Analytics/Analytics'))
Analytics.preload()

const TestingDashboard = lazyWithPreload(
  () => import('automation/dashboard/components/TestingDashboard')
)
TestingDashboard.preload()

const NotificationPage = lazyWithPreload(
  () => import('@/pages/NotificationCenter/NotificationPage')
)
NotificationPage.preload()

const Messages = lazyWithPreload(() => import('@/pages/Messages'))
Messages.preload()

// Flag-gated inside the page (Ask AttorneyShare); loaded on demand only.
const Assistant = lazyWithPreload(() => import('@/pages/Assistant'))

const isMaintenanceEnabled = IS_MAINTENANCE_ENABLED === 'true'

const SuspenseWrapper = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={<CustomLoading />}>{children}</Suspense>
)

export const Routes: Partial<
  Record<
    keyof typeof RoutesData,
    { element: ReactNode; errorElement: ReactNode }
  >
> = {
  RHome: {
    element: <Navigate to="/signIn" replace />,
    errorElement: <Error />,
  },
  RCreateAccount: {
    element: (
      <SuspenseWrapper>
        <DocumentTitle title={RoutesData.RCreateAccount.title} />
        <InviteAuthGuard>
          <CreateAccount />
        </InviteAuthGuard>
      </SuspenseWrapper>
    ),
    errorElement: <Error />,
  },
  RClaimInvite: {
    element: (
      <SuspenseWrapper>
        <DocumentTitle title={RoutesData.RClaimInvite.title} />
        <InviteAuthGuard>
          <CreateAccount />
        </InviteAuthGuard>
      </SuspenseWrapper>
    ),
    errorElement: <Error />,
  },
  RActivate: {
    element: (
      <SuspenseWrapper>
        <DocumentTitle title={RoutesData.RActivate.title} />
        <InviteAuthGuard>
          <MagicLinkActivate />
        </InviteAuthGuard>
      </SuspenseWrapper>
    ),
    errorElement: <Error />,
  },
  REditProfile: {
    element: (
      <AuthRoute>
        <SuspenseWrapper>
          <DocumentTitle title={RoutesData.REditProfile.title} />
          <EditPublicProfile />
        </SuspenseWrapper>
      </AuthRoute>
    ),
    errorElement: <Error />,
  },
  RProfile: {
    element: (
      <AuthRoute>
        <SuspenseWrapper>
          <DocumentTitle title={RoutesData.RProfile.title} />
          <PublicProfile />
        </SuspenseWrapper>
      </AuthRoute>
    ),
    errorElement: <Error />,
  },

  RMyNetwork: {
    element: (
      <AuthRoute>
        <SuspenseWrapper>
          <DocumentTitle title={RoutesData.RMyNetwork.title} />
          <MyNetwork />
        </SuspenseWrapper>
      </AuthRoute>
    ),
    errorElement: <Error />,
  },
  RMarketplace: {
    element: (
      <AuthRoute>
        <SuspenseWrapper>
          <DocumentTitle title={RoutesData.RMarketplace.title} />
          <Marketplace />
        </SuspenseWrapper>
      </AuthRoute>
    ),
    errorElement: <Error />,
  },
  RMyReferrals: {
    element: (
      <AuthRoute>
        <SuspenseWrapper>
          <DocumentTitle title={RoutesData.RMyReferrals.title} />
          <MyReferrals />
        </SuspenseWrapper>
      </AuthRoute>
    ),
    errorElement: <Error />,
  },
  RCreateProfile: {
    element: (
      <AuthRoute>
        <SuspenseWrapper>
          <DocumentTitle title={RoutesData.RCreateProfile.title} />
          <CreateProfile />
        </SuspenseWrapper>
      </AuthRoute>
    ),
    errorElement: <Error />,
  },
  RCreateNewCase: {
    element: (
      <AuthRoute>
        <SuspenseWrapper>
          <DocumentTitle title={RoutesData.RCreateNewCase.title} />
          <CreateNewCase />
        </SuspenseWrapper>
      </AuthRoute>
    ),
    errorElement: <Error />,
  },
  REditCase: {
    element: (
      <AuthRoute>
        <SuspenseWrapper>
          <DocumentTitle title={RoutesData.REditCase.title} />
          <EditCase />
        </SuspenseWrapper>
      </AuthRoute>
    ),
    errorElement: <Error />,
  },
  RSignIn: {
    element: (
      <AuthRoute>
        <SuspenseWrapper>
          <DocumentTitle title={RoutesData.RSignIn.title} />
          <SignIn />
        </SuspenseWrapper>
      </AuthRoute>
    ),
    errorElement: <Error />,
  },
  RSignOut: {
    element: <SignOut />,
    errorElement: <Error />,
  },
  RSettings: {
    element: (
      <AuthRoute>
        <SuspenseWrapper>
          <DocumentTitle title={RoutesData.RSettings.title} />
          <AccountSettings />
        </SuspenseWrapper>
      </AuthRoute>
    ),
    errorElement: <Error />,
  },
  RCase: {
    element: (
      <AuthRoute>
        <SuspenseWrapper>
          <DocumentTitle title={RoutesData.RCase.title} />
          <Case />
        </SuspenseWrapper>
      </AuthRoute>
    ),
    errorElement: <Error />,
  },
  RAdmin: {
    element: (
      <AuthRoute>
        <SuspenseWrapper>
          <DocumentTitle title={RoutesData.RAdmin.title} />
          <AdminPanel />
        </SuspenseWrapper>
      </AuthRoute>
    ),
    errorElement: <Error />,
  },
  RVerification: {
    element: (
      <SuspenseWrapper>
        <DocumentTitle title={RoutesData.RVerification.title} />
        <Verification />
      </SuspenseWrapper>
    ),
    errorElement: <Error />,
  },
  RResetPassword: {
    element: (
      <SuspenseWrapper>
        <DocumentTitle title={RoutesData.RResetPassword.title} />
        <ForgotPassword />
      </SuspenseWrapper>
    ),
    errorElement: <Error />,
  },
  RAnalytics: {
    element: (
      <AuthRoute>
        <SuspenseWrapper>
          <DocumentTitle title={RoutesData.RAnalytics.title} />
          <Analytics />
        </SuspenseWrapper>
      </AuthRoute>
    ),
    errorElement: <Error />,
  },
  RTestingDashboard: {
    element: (
      <AuthRoute>
        <SuspenseWrapper>
          <DocumentTitle title={RoutesData.RTestingDashboard.title} />
          <TestingDashboard />
        </SuspenseWrapper>
      </AuthRoute>
    ),
    errorElement: <Error />,
  },
  RNotifications: {
    element: (
      <AuthRoute>
        <SuspenseWrapper>
          <DocumentTitle title={RoutesData.RNotifications.title} />
          <NotificationPage />
        </SuspenseWrapper>
      </AuthRoute>
    ),
    errorElement: <Error />,
  },
  RMessages: {
    element: (
      <AuthRoute>
        <SuspenseWrapper>
          <DocumentTitle title={RoutesData.RMessages.title} />
          <Messages />
        </SuspenseWrapper>
      </AuthRoute>
    ),
    errorElement: <Error />,
  },
  RAssistant: {
    element: (
      <AuthRoute>
        <SuspenseWrapper>
          <DocumentTitle title={RoutesData.RAssistant.title} />
          <Assistant />
        </SuspenseWrapper>
      </AuthRoute>
    ),
    errorElement: <Error />,
  },
}

const routesArr = Object.entries(Routes).map(([key, route]) => ({
  ...route,
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  path: RoutesData[key as keyof typeof RoutesData].path,
}))

// Add redirect route for /admin to the first admin panel page
const adminRedirectRoute = {
  path: '/admin',
  element: <Navigate to={getAdminPanelFirstLink()} replace />,
}

// Redirect component for /myReferrals that respects persisted tab selection
const MyReferralsRedirect = () => {
  const { [FeatureFlag.MY_TASKS_ENABLED]: myTasksEnabled } = useFeatureFlags()
  const persisted = sessionStorageService.getItem(
    SessionKey.MY_REFERRALS_SELECTED_CATEGORY
  )

  // Derive valid routes from navigation constants, respecting feature flag
  const validRoutes = MY_REFERRALS_NAV_ITEMS.filter(item => {
    if (!item.route || item.disabled) return false
    // Exclude my-tasks if feature flag is off
    if (item.id === 'my-tasks' && !myTasksEnabled) return false
    return true
  }).map(item => item.route)

  // If persisted is 'my-tasks' but flag is OFF, clear it and use default
  if (persisted === 'my-tasks' && !myTasksEnabled) {
    sessionStorageService.setItem(
      SessionKey.MY_REFERRALS_SELECTED_CATEGORY,
      'inbound'
    )
  }

  // Use persisted route if valid, otherwise fall back to default
  const getDefaultRoute = () => {
    if (persisted && validRoutes.includes(persisted)) {
      return persisted
    }
    if (myTasksEnabled) {
      return getDefaultNavItem().route
    }
    return 'inbound'
  }
  const defaultRoute = getDefaultRoute()

  // Persist the route so context initializes with the correct value
  sessionStorageService.setItem(
    SessionKey.MY_REFERRALS_SELECTED_CATEGORY,
    defaultRoute
  )

  return (
    <Navigate
      to={`${PATHS_WITH_SECTIONS.MY_REFERRALS}/${defaultRoute}`}
      replace
    />
  )
}

// Add redirect route for /myReferrals to the first referrals page
const myReferralsRedirectRoute = {
  path: '/myReferrals',
  element: <MyReferralsRedirect />,
}

const rootRouter = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      ...routesArr.map(r => ({
        element: isMaintenanceEnabled ? <Maintenance /> : r.element,
        errorElement: r.errorElement,
        path: r.path,
      })),
      adminRedirectRoute,
      myReferralsRedirectRoute,
    ],
  },
])

export default rootRouter
